import { Button } from "@package/subframe/components/Button";
import { Select } from "@package/subframe/components/Select";
import { TextArea } from "@package/subframe/components/TextArea";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherAlertTriangle } from "@subframe/core";
import { useMemo, useState } from "react";

import { HOT_HOLDING_MIN_TEMP_C } from "@/hooks/use-offline-hot-holding-board";

type ReadingType = "DELIVERY" | "SERVICE";

/**
 * Form for a single hot holding reading (NAU-93).
 *   - `delivery` mode opens a new meal: meal name + delivery temperature.
 *   - `reading` mode adds to an existing meal: reading type (delivery/service)
 *     + temperature.
 * A reading at or below the hot holding floor shows a warning and makes the
 * follow-up note mandatory before submit, mirroring the server invariant. The
 * floor is the org-group's configured "Hot holding policy" (NAU-220), passed in
 * as `minTempC` and falling back to the default when unset.
 */
interface HotHoldingReadingFormProps {
  mode: "delivery" | "reading";
  onSubmit: (args: {
    mealName?: string;
    readingType: ReadingType;
    temperature: number;
    notes?: string;
  }) => Promise<void>;
  isSubmitting: boolean;
  /** Org-configured hot holding floor (°C). Defaults to the constant when unset. */
  minTempC?: number;
}

export default function HotHoldingReadingForm({
  mode,
  onSubmit,
  isSubmitting,
  minTempC,
}: HotHoldingReadingFormProps) {
  const minTemp = minTempC ?? HOT_HOLDING_MIN_TEMP_C;
  const [mealName, setMealName] = useState("");
  const [readingType, setReadingType] = useState<ReadingType>(
    mode === "delivery" ? "DELIVERY" : "SERVICE",
  );
  const [temperatureRaw, setTemperatureRaw] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const parsedTemperature = useMemo(() => {
    if (temperatureRaw.trim() === "") return null;
    const n = Number(temperatureRaw);
    return Number.isFinite(n) ? n : null;
  }, [temperatureRaw]);

  const isOutOfRange =
    parsedTemperature !== null && parsedTemperature < minTemp;
  const noteMissing = isOutOfRange && notes.trim().length === 0;
  const mealNameMissing = mode === "delivery" && mealName.trim().length === 0;
  const submitDisabled =
    isSubmitting ||
    parsedTemperature === null ||
    mealNameMissing ||
    noteMissing;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (parsedTemperature === null) {
      setError("Please enter a numeric temperature.");
      return;
    }
    if (mealNameMissing) {
      setError("Please enter a meal name.");
      return;
    }
    if (noteMissing) {
      setError(
        "A note is required when the temperature is below the hot holding floor.",
      );
      return;
    }
    try {
      await onSubmit({
        mealName: mode === "delivery" ? mealName.trim() : undefined,
        readingType,
        temperature: parsedTemperature,
        notes: notes.trim() || undefined,
      });
      setMealName("");
      setTemperatureRaw("");
      setNotes("");
      setReadingType(mode === "delivery" ? "DELIVERY" : "SERVICE");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save reading");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-4">
      {mode === "delivery" && (
        <TextField label="Meal name">
          <TextField.Input
            placeholder="e.g. Chicken curry"
            value={mealName}
            onChange={(e) => setMealName(e.target.value)}
            disabled={isSubmitting}
            autoFocus
          />
        </TextField>
      )}

      {mode === "reading" && (
        <Select
          label="Reading type"
          value={readingType}
          onValueChange={(v) => setReadingType(v as ReadingType)}
        >
          <Select.Item value="SERVICE">Service</Select.Item>
          <Select.Item value="DELIVERY">Delivery</Select.Item>
        </Select>
      )}

      <TextField
        label="Temperature (°C)"
        helpText={`Must be ${minTemp}°C or above`}
      >
        <TextField.Input
          type="number"
          step="0.1"
          inputMode="decimal"
          placeholder="e.g. 78"
          value={temperatureRaw}
          onChange={(e) => setTemperatureRaw(e.target.value)}
          disabled={isSubmitting}
          autoFocus={mode === "reading"}
        />
      </TextField>

      {isOutOfRange && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-md border border-warning-200 bg-warning-50 p-3 text-warning-900"
        >
          <FeatherAlertTriangle className="mt-0.5 h-5 w-5 flex-none" />
          <div className="flex flex-col gap-1">
            <span className="text-body-bold font-body-bold">
              Below the hot holding temperature
            </span>
            <span className="text-caption font-caption">
              Temperature is below the minimum hot holding temperature of{" "}
              {minTemp}°C. Check your company&apos;s policies and action as
              required, and make note of actions below.
            </span>
          </div>
        </div>
      )}

      <TextArea
        label={
          isOutOfRange ? "Follow-up actions (required)" : "Notes (optional)"
        }
        error={noteMissing}
      >
        <TextArea.Input
          placeholder="Please note any follow-up actions you have taken to rectify the issue."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isSubmitting}
        />
      </TextArea>

      {error && (
        <span className="text-body font-body text-error-700">{error}</span>
      )}

      <Button
        type="submit"
        variant="brand-primary"
        size="medium"
        loading={isSubmitting}
        disabled={submitDisabled}
      >
        {mode === "delivery" ? "Log delivery" : "Save reading"}
      </Button>
    </form>
  );
}
